import { LitElement, html, css, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";
import "../components/index.js";
import "./usage-chart.js";
import type { UsagePoint } from "./usage-chart.js";
import { toast } from "../components/index.js";
import { listTeams, type Team } from "../api/teams.js";
import { getUsage, type UsageReport, type UsageDimensions } from "../api/usage.js";
import { humanError } from "../api/errors.js";

// The five metered dimensions, each with its label and a with-unit formatter
// shared by the KPI tile, the chart axis, and the chart tooltip.
interface Dim {
  key: keyof UsageDimensions;
  label: string;
  format: (n: number) => string;
  axis: (n: number) => string;
}

const fmtInt = (n: number) => Math.round(n).toLocaleString("en-US");

/** Compact axis number: integers plain, otherwise one decimal — never a trailing ".0". */
const fmtAxisNum = (n: number) => {
  const r = Math.round(n * 10) / 10;
  return Number.isInteger(r) ? String(r) : r.toFixed(1);
};

const DIMENSIONS: Dim[] = [
  {
    key: "build_count",
    label: "Builds",
    format: (n) => `${fmtInt(n)} builds`,
    axis: (n) => fmtAxisNum(n),
  },
  {
    key: "compute_seconds",
    label: "Compute",
    format: (n) =>
      n >= 3600 ? `${(n / 3600).toFixed(1)} h` : n >= 60 ? `${(n / 60).toFixed(1)} m` : `${Math.round(n)} s`,
    axis: (n) =>
      n >= 3600 ? `${fmtAxisNum(n / 3600)}h` : n >= 60 ? `${fmtAxisNum(n / 60)}m` : `${Math.round(n)}s`,
  },
  {
    key: "preview_instance_hours",
    label: "Preview",
    format: (n) => `${n >= 10 ? n.toFixed(0) : n.toFixed(1)} h`,
    axis: (n) => `${fmtAxisNum(n)}h`,
  },
  {
    key: "storage_gb",
    label: "Storage",
    format: (n) => `${n >= 10 ? n.toFixed(1) : n.toFixed(2)} GB`,
    axis: (n) => `${fmtAxisNum(n)} GB`,
  },
  {
    key: "live_instance_hours",
    label: "Live",
    format: (n) => `${n >= 10 ? n.toFixed(0) : n.toFixed(1)} h`,
    axis: (n) => `${fmtAxisNum(n)}h`,
  },
];

const PRESETS = [7, 30, 90];

function utcToday(): string {
  return new Date().toISOString().slice(0, 10);
}

/** N days before today, UTC (YYYY-MM-DD). */
function daysAgo(n: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - n);
  return d.toISOString().slice(0, 10);
}

/** The exclusive end (inclusiveTo + 1 day) the server expects for [from, to). */
function exclusiveEnd(inclusiveTo: string): string {
  const d = new Date(`${inclusiveTo}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}

/** All UTC days in [from, to] inclusive. */
function enumerateDays(from: string, to: string): string[] {
  const out: string[] = [];
  const d = new Date(`${from}T00:00:00Z`);
  const end = new Date(`${to}T00:00:00Z`);
  while (d <= end) {
    out.push(d.toISOString().slice(0, 10));
    d.setUTCDate(d.getUTCDate() + 1);
  }
  return out;
}

/**
 * page-usage is the metered-usage dashboard (/usage). It reads GET /v1/usage for
 * the selected team over an inclusive date window and shows a per-dimension KPI
 * tile row plus a small daily bar chart per dimension. The window inputs are
 * inclusive; the API call converts the end to the server's exclusive bound. The
 * team + date-range controls drive a re-fetch; a request-id guards against a
 * slower earlier response overwriting a newer one.
 */
@customElement("page-usage")
export class PageUsage extends LitElement {
  @state() private teams?: Team[];
  @state() private teamId = "";
  @state() private from = daysAgo(29);
  @state() private to = utcToday();
  @state() private preset: number | null = 30;
  @state() private report?: UsageReport;
  @state() private loading = false;
  @state() private error = "";

  private reqId = 0;

  static override styles = css`
    :host {
      display: block;
    }
    .head {
      display: flex;
      align-items: flex-end;
      justify-content: space-between;
      gap: var(--bino-space-lg);
      flex-wrap: wrap;
      margin-bottom: var(--bino-space-xl);
    }
    h1 {
      margin: 0;
      font-size: var(--bino-font-size-xl);
      font-weight: 600;
      color: var(--bino-text);
    }
    .controls {
      display: flex;
      gap: var(--bino-space-md);
      flex-wrap: wrap;
      align-items: flex-end;
    }
    .ctl {
      display: flex;
      flex-direction: column;
    }
    .ctl > span {
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-sm);
      font-weight: 500;
      color: var(--bino-text-muted);
      margin-bottom: 0.375rem;
    }
    select,
    input[type="date"] {
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-md);
      color: var(--bino-text);
      background: var(--bino-surface-subtle);
      border: 1px solid var(--bino-border-light);
      border-radius: var(--bino-radius);
      padding: 0.5rem 0.6875rem;
      transition:
        border-color var(--bino-transition-fast),
        box-shadow var(--bino-transition-fast);
    }
    select:focus,
    input[type="date"]:focus {
      outline: none;
      border-color: var(--bino-primary);
      box-shadow: 0 0 0 3px var(--bino-focus-ring);
    }
    .presets {
      display: inline-flex;
      gap: var(--bino-space-xs);
    }
    .updating {
      display: flex;
      align-items: center;
      gap: var(--bino-space-sm);
      margin-bottom: var(--bino-space-md);
    }
    .kpis {
      display: grid;
      grid-template-columns: repeat(5, 1fr);
      gap: var(--bino-space-md);
      margin-bottom: var(--bino-space-xl);
    }
    .tile {
      border: 1px solid var(--bino-border);
      border-radius: var(--bino-radius-lg);
      background: var(--bino-surface);
      box-shadow: var(--bino-shadow-header);
      padding: var(--bino-space-lg);
    }
    .tile .eyebrow {
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-sm);
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: var(--bino-text-secondary);
    }
    .tile .num {
      margin-top: var(--bino-space-sm);
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-xl);
      font-weight: 600;
      line-height: 1.1;
      color: var(--bino-text);
    }
    .charts {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: var(--bino-space-lg);
    }
    @media (max-width: 900px) {
      .kpis {
        grid-template-columns: repeat(2, 1fr);
      }
    }
    @media (max-width: 820px) {
      .charts {
        grid-template-columns: 1fr;
      }
    }
    @media (max-width: 520px) {
      .kpis {
        grid-template-columns: 1fr;
      }
    }
  `;

  override connectedCallback() {
    super.connectedCallback();
    void this.init();
  }

  private async init() {
    this.loading = true;
    try {
      const teams = await listTeams();
      this.teams = teams;
      if (teams.length === 0) {
        this.loading = false;
        return;
      }
      this.teamId = teams[0].id;
      await this.load();
    } catch (e) {
      this.error = humanError(e);
      this.loading = false;
    }
  }

  private async load() {
    if (!this.teamId) return;
    if (this.from > this.to) {
      toast("Start date is after end date.", "warning");
      return;
    }
    const id = ++this.reqId;
    this.loading = true;
    this.error = "";
    try {
      const report = await getUsage(this.teamId, { from: this.from, to: exclusiveEnd(this.to) });
      if (id !== this.reqId) return;
      this.report = report;
    } catch (e) {
      if (id !== this.reqId) return;
      this.error = humanError(e);
      toast(this.error, "error");
    } finally {
      if (id === this.reqId) this.loading = false;
    }
  }

  private onTeam(e: Event) {
    this.teamId = (e.target as HTMLSelectElement).value;
    void this.load();
  }

  private onDate(which: "from" | "to", e: Event) {
    const v = (e.target as HTMLInputElement).value;
    if (!v) return;
    this[which] = v;
    this.preset = null;
    void this.load();
  }

  private onPreset(days: number) {
    this.preset = days;
    this.from = daysAgo(days - 1);
    this.to = utcToday();
    void this.load();
  }

  private pointsFor(dim: Dim): UsagePoint[] {
    const byDate = new Map(this.report?.series.map((d) => [d.date, d]) ?? []);
    return enumerateDays(this.from, this.to).map((date) => ({
      date,
      value: byDate.get(date)?.[dim.key] ?? 0,
    }));
  }

  private renderControls() {
    const teams = this.teams ?? [];
    return html`
      <div class="controls">
        <label class="ctl">
          <span>Team</span>
          <select @change=${this.onTeam} .value=${this.teamId} aria-label="Team">
            ${teams.map(
              (t) => html`<option value=${t.id} ?selected=${t.id === this.teamId}>${t.displayName}</option>`,
            )}
          </select>
        </label>
        <label class="ctl">
          <span>From</span>
          <input
            type="date"
            .value=${this.from}
            max=${this.to}
            @change=${(e: Event) => this.onDate("from", e)}
          />
        </label>
        <label class="ctl">
          <span>To</span>
          <input
            type="date"
            .value=${this.to}
            min=${this.from}
            max=${utcToday()}
            @change=${(e: Event) => this.onDate("to", e)}
          />
        </label>
        <div class="ctl">
          <span>Range</span>
          <div class="presets" role="group" aria-label="Quick date range">
            ${PRESETS.map(
              (d) => html`<bn-button
                size="sm"
                variant=${this.preset === d ? "primary" : "secondary"}
                aria-pressed=${this.preset === d ? "true" : "false"}
                @click=${() => this.onPreset(d)}
                >${d}d</bn-button
              >`,
            )}
          </div>
        </div>
      </div>
    `;
  }

  private renderBody() {
    if (this.loading && !this.report) {
      return html`<bn-spinner block label="Loading usage…"></bn-spinner>`;
    }
    if (this.error && !this.report) {
      return html`<bn-empty-state
        icon="!"
        heading="Couldn't load usage"
        message=${this.error}
      >
        <bn-button slot="actions" variant="primary" @click=${() => this.load()}>Retry</bn-button>
      </bn-empty-state>`;
    }
    const report = this.report;
    if (!report) return nothing;
    return html`
      ${this.loading
        ? html`<div class="updating"><bn-spinner size=${14} label="Updating…"></bn-spinner></div>`
        : nothing}
      <div class="kpis">
        ${DIMENSIONS.map(
          (dim) => html`<div class="tile">
            <div class="eyebrow">${dim.label}</div>
            <div class="num">${dim.format(report.totals[dim.key])}</div>
          </div>`,
        )}
      </div>
      <div class="charts">
        ${DIMENSIONS.map(
          (dim) => html`<bn-card heading=${dim.label}>
            <usage-chart
              .label=${dim.label}
              .points=${this.pointsFor(dim)}
              .format=${dim.format}
              .axisFormat=${dim.axis}
            ></usage-chart>
          </bn-card>`,
        )}
      </div>
    `;
  }

  override render() {
    if (this.teams && this.teams.length === 0) {
      return html`<bn-empty-state
        icon="◷"
        heading="No teams yet"
        message="Usage is metered per team. Create a team to see its usage."
      ></bn-empty-state>`;
    }
    return html`
      <div class="head">
        <div>
          <h1>Usage</h1>
        </div>
        ${this.teams ? this.renderControls() : nothing}
      </div>
      ${this.renderBody()}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "page-usage": PageUsage;
  }
}
